import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { HOME_BY_ROLE } from "@/components/ProtectedRoute";
import { User, Building2, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { usePageMeta } from "@/lib/usePageMeta";

export default function Register() {
  const { register } = useAuth();
  usePageMeta("Create your account · Suffle Online", "Create a free Suffle Online account — the AI-powered marketplace. Shop, discover and buy from verified sellers with Suffle AI.");
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [role, setRole] = useState(sp.get("role") === "travel_agent" ? "travel_agent" : "customer");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", company_name: "", offering_type: "products", referral_code: sp.get("ref") || "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await register({ ...form, role });
      if (u && u.needs_verification) { setSent(true); return; }
      toast.success("Account created!");
      navigate(u.role === "customer" ? "/" : (HOME_BY_ROLE[u.role] || "/app/home"));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally { setLoading(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (sent) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6" style={{ backgroundColor: "#0b0a1f", backgroundImage: "radial-gradient(55% 55% at 15% 10%, rgba(37,99,235,.45), transparent 60%), radial-gradient(55% 55% at 85% 15%, rgba(124,58,237,.45), transparent 60%), radial-gradient(70% 60% at 60% 100%, rgba(219,39,119,.26), transparent 60%)" }}>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-xl" data-testid="verify-sent">
          <Logo variant="lg" />
          <div className="mx-auto mt-6 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-500"><MailCheck className="h-7 w-7" /></div>
          <h1 className="mt-4 font-brand text-2xl font-bold text-slate-800">Check your inbox</h1>
          <p className="mt-2 text-sm text-slate-500">We've sent a verification link to <span className="font-semibold text-slate-700">{form.email}</span>. Click it to activate your account, then log in.</p>
          <Link to="/login" className="mt-6 inline-block w-full rounded-xl brand-gradient-bg py-3 font-semibold text-white">Go to Login</Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-6" style={{ backgroundColor: "#0b0a1f", backgroundImage: "radial-gradient(55% 55% at 15% 10%, rgba(37,99,235,.45), transparent 60%), radial-gradient(55% 55% at 85% 15%, rgba(124,58,237,.45), transparent 60%), radial-gradient(70% 60% at 60% 100%, rgba(219,39,119,.26), transparent 60%)" }}>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md rounded-2xl bg-white p-7 shadow-xl">
        <Logo variant="lg" />
        <h1 className="mt-5 font-brand text-2xl font-bold text-slate-800">Create your account</h1>
        <p className="mt-1 text-sm text-slate-500">Start shopping smarter with Suffle AI — for free.</p>

        <div className="mt-5 grid grid-cols-2 gap-2">
          <button onClick={() => setRole("customer")} type="button" data-testid="role-customer"
            className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-medium ${role === "customer" ? "border-blue-400 bg-blue-50 text-blue-600" : "border-slate-200 text-slate-500"}`}>
            <User className="h-4 w-4" /> I'm a Customer
          </button>
          <button onClick={() => setRole("travel_agent")} type="button" data-testid="role-agent"
            className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-medium ${role === "travel_agent" ? "border-blue-400 bg-blue-50 text-blue-600" : "border-slate-200 text-slate-500"}`}>
            <Building2 className="h-4 w-4" /> Seller
          </button>
        </div>

        {error && <div className="mt-4 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-600" data-testid="register-error">{error}</div>}

        <form onSubmit={submit} className="mt-4 space-y-3" data-testid="register-form">
          <input value={form.name} onChange={set("name")} required placeholder="Full name" data-testid="reg-name"
            className="w-full rounded-xl border border-slate-200 px-4 py-2.5 outline-none focus:border-blue-400" />
          {role === "travel_agent" && (
            <input value={form.company_name} onChange={set("company_name")} placeholder="Store name" data-testid="reg-company"
              className="w-full rounded-xl border border-slate-200 px-4 py-2.5 outline-none focus:border-blue-400" />
          )}
          {role === "travel_agent" && (
            <div data-testid="reg-offering">
              <p className="mb-1.5 text-xs font-semibold text-slate-500">What will you offer?</p>
              <div className="grid grid-cols-3 gap-2">
                {[["products", "Products"], ["services", "Services"], ["both", "Both"]].map(([val, label]) => (
                  <button key={val} type="button" onClick={() => setForm({ ...form, offering_type: val })} data-testid={`reg-offering-${val}`}
                    className={`rounded-xl border px-2 py-2 text-xs font-semibold ${form.offering_type === val ? "border-blue-400 bg-blue-50 text-blue-600" : "border-slate-200 text-slate-500"}`}>{label}</button>
                ))}
              </div>
              <p className="mt-1.5 text-[11px] text-slate-400">{form.offering_type === "products" ? "You'll list physical products. Standard seller verification applies." : form.offering_type === "services" ? "You'll list bookable services. Each service category may require its own verification documents." : "List products and services. Verification requirements apply per category."}</p>
            </div>
          )}
          <input value={form.email} onChange={set("email")} type="email" required placeholder="Email" data-testid="reg-email"
            className="w-full rounded-xl border border-slate-200 px-4 py-2.5 outline-none focus:border-blue-400" />
          <input value={form.phone} onChange={set("phone")} placeholder="Phone (optional)" data-testid="reg-phone"
            className="w-full rounded-xl border border-slate-200 px-4 py-2.5 outline-none focus:border-blue-400" />
          <input value={form.password} onChange={set("password")} type="password" required minLength={6} placeholder="Password (min 6 chars)" data-testid="reg-password"
            className="w-full rounded-xl border border-slate-200 px-4 py-2.5 outline-none focus:border-blue-400" />
          <input value={form.referral_code} onChange={set("referral_code")} placeholder="Referral code (optional)" data-testid="reg-referral"
            className="w-full rounded-xl border border-slate-200 px-4 py-2.5 outline-none focus:border-blue-400" />
          <button type="submit" disabled={loading} data-testid="register-submit"
            className="w-full rounded-xl brand-gradient-bg py-3 font-semibold text-white disabled:opacity-50">
            {loading ? "Creating..." : "Create Account"}
          </button>
        </form>
        <p className="mt-4 text-center text-sm text-slate-500">Already have an account? <Link to="/login" className="font-semibold text-blue-600">Log in</Link></p>
      </motion.div>
    </div>
  );
}
